import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import logger from '@/lib/logger';
import { useAuth } from '@/context/AuthContext';
import {
  Users, CurrencyDollar, ChartLineUp, ChatCircle, Crown, Lightning, ArrowsClockwise, Lock, Gift,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, t] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users?limit=10`),
        axios.get(`${API}/support/tickets`),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setTickets(t.data.slice(0, 10));
    } catch (err) {
      logger.error('Admin load err:', err);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="admin-forbidden">
        <div className="text-center max-w-md">
          <Lock weight="fill" className="w-16 h-16 text-[#FF3B30] mx-auto mb-4" />
          <h1 className="text-3xl font-black tracking-tighter mb-2">ADMIN ONLY</h1>
          <p className="text-[#A1A1AA] text-sm mb-6">This page is restricted to admin accounts.</p>
          <Link to="/dashboard" className="btn-primary px-6 py-3 text-sm">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  if (loading || !stats) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="admin-loading">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { users: u, growth, engagement, revenue, support, referrals } = stats;

  return (
    <div data-testid="admin-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="label-tactical text-[#FF3B30] mb-1">Internal</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">ADMIN</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            Business health · updated {new Date(stats.generated_at).toLocaleTimeString()}
          </p>
        </div>
        <button onClick={load} className="btn-secondary px-4 py-2 text-sm flex items-center gap-2" data-testid="admin-refresh">
          <ArrowsClockwise weight="bold" className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Top-line KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="tactical-card p-4" data-testid="kpi-total-users">
          <Users weight="bold" className="w-5 h-5 text-[#007AFF] mb-2" />
          <div className="text-3xl font-black font-['Barlow_Condensed']">{u.total}</div>
          <div className="label-tactical text-[#71717A] mt-1">Total users</div>
        </div>
        <div className="tactical-card p-4" data-testid="kpi-pro">
          <Crown weight="fill" className="w-5 h-5 text-[#FF9500] mb-2" />
          <div className="text-3xl font-black font-['Barlow_Condensed']">{u.pro}</div>
          <div className="label-tactical text-[#71717A] mt-1">Pro · {u.pro_conversion_pct}%</div>
        </div>
        <div className="tactical-card p-4" data-testid="kpi-mrr">
          <CurrencyDollar weight="bold" className="w-5 h-5 text-[#34C759] mb-2" />
          <div className="text-3xl font-black font-['Barlow_Condensed']">${revenue.mrr_usd}</div>
          <div className="label-tactical text-[#71717A] mt-1">30-day revenue</div>
        </div>
        <div className="tactical-card p-4" data-testid="kpi-workouts">
          <Lightning weight="fill" className="w-5 h-5 text-[#FF3B30] mb-2" />
          <div className="text-3xl font-black font-['Barlow_Condensed']">{engagement.total_workouts_completed}</div>
          <div className="label-tactical text-[#71717A] mt-1">Total workouts</div>
        </div>
      </div>

      {/* Growth + Engagement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="tactical-card p-5" data-testid="growth-card">
          <div className="flex items-center gap-2 mb-4">
            <ChartLineUp weight="bold" className="w-5 h-5 text-[#007AFF]" />
            <h2 className="text-lg font-bold tracking-tight">Growth</h2>
          </div>
          <div className="space-y-3">
            <GrowthRow label="Sign-ups today" value={growth.signups_today} />
            <GrowthRow label="Sign-ups this week" value={growth.signups_week} />
            <GrowthRow label="Sign-ups this month" value={growth.signups_month} />
          </div>
        </div>

        <div className="tactical-card p-5" data-testid="engagement-card">
          <div className="flex items-center gap-2 mb-4">
            <Lightning weight="bold" className="w-5 h-5 text-[#FF3B30]" />
            <h2 className="text-lg font-bold tracking-tight">Engagement</h2>
          </div>
          <div className="space-y-3">
            <GrowthRow label="Workouts this week" value={engagement.workouts_this_week} />
            <GrowthRow label="Push subscriptions" value={engagement.push_subscriptions} />
            <GrowthRow label="Active buddy pairs" value={engagement.active_buddy_pairs} />
            <GrowthRow label="Public profiles" value={u.public_profile} />
          </div>
        </div>
      </div>

      {/* Referral Funnel */}
      {referrals && (
        <div className="tactical-card p-5 mb-6" data-testid="referrals-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Gift weight="fill" className="w-5 h-5 text-[#FF9500]" />
              <h2 className="text-lg font-bold tracking-tight">Referral funnel</h2>
            </div>
            <span className="text-xs text-[#71717A]">
              qualify rate: <b className="text-white">{referrals.qualify_rate_pct}%</b>
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Signed up" value={referrals.total} color="#007AFF" testid="ref-stat-total" />
            <MiniStat label="Qualified" value={referrals.qualified} color="#34C759" testid="ref-stat-qualified" />
            <MiniStat label="This week" value={referrals.signups_this_week} color="#FF3B30" testid="ref-stat-week" />
            <MiniStat label="Pro days granted" value={referrals.pro_days_granted} color="#FF9500" testid="ref-stat-days" />
          </div>
        </div>
      )}

      {/* Recent users */}
      <div className="tactical-card p-5 mb-6" data-testid="recent-users">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users weight="bold" className="w-5 h-5 text-[#007AFF]" />
            <h2 className="text-lg font-bold tracking-tight">Recent sign-ups</h2>
          </div>
          <span className="text-xs text-[#71717A]">Latest {users.length}</span>
        </div>
        {users.length === 0 ? (
          <p className="text-sm text-[#71717A]">No users yet.</p>
        ) : (
          <div className="space-y-2">
            {users.map((usr, i) => (
              <div key={usr.email || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{usr.name || '(no name)'}</div>
                  <div className="text-xs text-[#71717A] truncate">{usr.email}</div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {usr.is_pro && <Crown weight="fill" className="w-3 h-3 text-[#FF9500]" />}
                  <span className="text-[#71717A]">{usr.created_at ? new Date(usr.created_at).toLocaleDateString() : '—'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Support queue */}
      <div className="tactical-card p-5 mb-24 lg:mb-6" data-testid="support-queue">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ChatCircle weight="bold" className="w-5 h-5 text-[#FF9500]" />
            <h2 className="text-lg font-bold tracking-tight">Support queue</h2>
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider ${support.open_tickets > 0 ? 'text-[#FF9500]' : 'text-[#71717A]'}`}>
            {support.open_tickets} open
          </span>
        </div>
        {tickets.length === 0 ? (
          <p className="text-sm text-[#71717A]">No tickets yet.</p>
        ) : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <div key={t.ticket_id} className="p-3 bg-[#1C1C1E] border border-white/5">
                <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                  <span className="text-xs font-mono text-[#FF3B30]">#{t.ticket_id}</span>
                  <span className="text-xs text-[#71717A]">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</span>
                </div>
                <div className="font-bold text-sm">{t.subject}</div>
                <div className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">{t.message}</div>
                <div className="text-xs text-[#71717A] mt-1">from {t.name} · {t.email}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GrowthRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-[#A1A1AA]">{label}</span>
      <span className="text-xl font-black font-['Barlow_Condensed'] text-white">{value}</span>
    </div>
  );
}

function MiniStat({ label, value, color, testid }) {
  return (
    <div className="bg-[#1C1C1E] border border-white/5 p-3" data-testid={testid}>
      <div className="text-2xl font-black font-['Barlow_Condensed']" style={{ color }}>{value}</div>
      <div className="label-tactical text-[#71717A] mt-0.5">{label}</div>
    </div>
  );
}
